/* ============================================================
   KinkKart track-order (2026-10-11): <kk-track>.
   Posts { order, contact } to the app proxy (data-endpoint) and
   draws the answer. Everything from the server goes in through
   textContent, never innerHTML.
   ============================================================ */
(() => {
  if (customElements.get("kk-track")) return;

  const STEPS = [
    { key: "placed", label: "Order placed" },
    { key: "printing", label: "Printing" },
    { key: "shipped", label: "Shipped" },
    { key: "delivered", label: "Delivered" },
  ];
  const BADGES = {
    placed: "Received",
    printing: "Printing",
    shipped: "On its way",
    delivered: "Delivered",
    cancelled: "Cancelled",
  };
  const LABEL_ONLY = new Set(["LABEL_PURCHASED", "LABEL_PRINTED", "CONFIRMED", "SUBMITTED"]);

  const day = new Intl.DateTimeFormat("en-US", { weekday: "short", month: "short", day: "numeric" });
  const dayUtc = new Intl.DateTimeFormat("en-US", { weekday: "short", month: "short", day: "numeric", timeZone: "UTC" });
  const short = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" });
  const stamp = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

  function h(tag, props, ...children) {
    const el = document.createElement(tag);
    for (const [k, v] of Object.entries(props || {})) {
      if (v == null || v === false) continue;
      if (k === "class") el.className = v;
      else if (k === "text") el.textContent = v;
      else if (k.startsWith("on")) el.addEventListener(k.slice(2), v);
      else el.setAttribute(k, v === true ? "" : v);
    }
    for (const c of children.flat()) {
      if (c == null || c === false) continue;
      el.append(c instanceof Node ? c : document.createTextNode(String(c)));
    }
    return el;
  }

  const fmtDay = (iso) => (iso ? day.format(new Date(iso)) : "");
  const fmtYmd = (ymd) => (ymd ? dayUtc.format(new Date(`${ymd}T12:00:00Z`)) : "");

  class KkTrack extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector("[data-kk-track-form]");
      this.error = this.querySelector("[data-kk-track-error]");
      this.submit = this.querySelector("[data-kk-track-submit]");
      this.result = this.querySelector("[data-kk-track-result]");
      this.orderInput = this.form.elements.order;
      this.contactInput = this.form.elements.contact;
      this.endpoint = this.dataset.endpoint || "/apps/kk-track/lookup";
      this.contact = this.dataset.contact || "contact@kinkkart.com";

      const fromUrl = new URLSearchParams(location.search).get("order");
      if (fromUrl) this.orderInput.value = fromUrl.replace(/[^\d#]/g, "").slice(0, 12);

      this.form.addEventListener("submit", (e) => {
        e.preventDefault();
        this.lookup();
      });
      for (const input of [this.orderInput, this.contactInput]) {
        input.addEventListener("input", () => input.removeAttribute("aria-invalid"));
      }
    }

    mailLink(text) {
      return h("a", { href: `mailto:${this.contact}` }, text || this.contact);
    }

    showError(message, field) {
      this.error.replaceChildren(...[].concat(message));
      this.error.hidden = false;
      if (field) {
        field.setAttribute("aria-invalid", "true");
        field.focus();
      }
    }

    clearError() {
      this.error.hidden = true;
      this.error.replaceChildren();
      this.orderInput.removeAttribute("aria-invalid");
      this.contactInput.removeAttribute("aria-invalid");
    }

    async lookup() {
      this.clearError();
      const order = this.orderInput.value.replace(/[\s#]/g, "");
      const contact = this.contactInput.value.trim();

      if (!/^\d{3,10}$/.test(order)) {
        return this.showError("Enter the order number from your confirmation email, like 1001.", this.orderInput);
      }
      const isEmail = contact.includes("@");
      if (isEmail ? !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact) : contact.replace(/\D/g, "").length < 7) {
        return this.showError("Enter the email you ordered with, or the phone number on the shipping address.", this.contactInput);
      }

      this.submit.setAttribute("aria-busy", "true");
      this.submit.disabled = true;
      let res;
      let body = null;
      try {
        res = await fetch(this.endpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ order, contact }),
          credentials: "same-origin",
        });
        if ((res.headers.get("Content-Type") || "").includes("application/json")) body = await res.json();
      } catch {
        body = null;
      } finally {
        this.submit.removeAttribute("aria-busy");
        this.submit.disabled = false;
      }

      if (body && body.ok && body.order) return this.render(body.order);

      const code = body && body.error;
      if (code === "not_found") {
        this.showError([
          "We couldn't find an order with that number and email or phone. Check both against your confirmation email, or write to ",
          this.mailLink(),
          ".",
        ]);
      } else if (code === "bad_input") {
        this.showError("Check the order number: digits only, like 1001.", this.orderInput);
      } else if (code === "rate_limited") {
        this.showError("That's a lot of tries in a row. Wait a minute, then try again.");
      } else {
        this.showError([
          "Tracking is having a moment. The tracking link in your shipping email still works, or write to ",
          this.mailLink(),
          " and we'll check for you.",
        ]);
      }
    }

    render(o) {
      const stage = o.stage;
      const shipments = o.shipments || [];
      const reached = stage === "cancelled" ? 0 : STEPS.findIndex((s) => s.key === stage);

      const dates = {
        placed: o.placedAt,
        shipped: shipments.map((s) => s.shippedAt).filter(Boolean).sort()[0],
        delivered: stage === "delivered" ? shipments.map((s) => s.deliveredAt).filter(Boolean).sort().pop() : null,
      };

      const head = h(
        "header",
        { class: "kk-track__head" },
        h(
          "div",
          null,
          h("h2", { class: "kk-track__number", text: `Order ${o.number}` }),
          h(
            "p",
            { class: "kk-track__meta" },
            `Placed ${fmtDay(o.placedAt)}`,
            o.destination ? ` · Shipping to ${o.destination}` : "",
          ),
        ),
        h("span", { class: `kk-track__badge kk-track__badge--${stage}`, text: BADGES[stage] || "In progress" }),
      );

      const progress =
        stage === "cancelled"
          ? null
          : h(
              "ol",
              { class: "kk-track__steps", style: `--kk-progress: ${reached / (STEPS.length - 1)}`, "aria-label": "Order progress" },
              STEPS.map((s, i) => {
                const state = i < reached || (i === reached && stage === "delivered") ? "is-done" : i === reached ? "is-current" : "";
                return h(
                  "li",
                  { class: `kk-track__step ${state}`, "aria-current": state === "is-current" ? "step" : null },
                  h("span", { class: "kk-track__dot", "aria-hidden": "true" }),
                  h("span", { class: "kk-track__step-label", text: s.label }),
                  dates[s.key] && i <= reached ? h("span", { class: "kk-track__step-date", text: short.format(new Date(dates[s.key])) }) : null,
                );
              }),
            );

      const status = h("p", { class: "kk-track__status" }, ...this.statusText(o));

      let eta = null;
      if (o.estimate && o.estimate.from) {
        const same = o.estimate.from === o.estimate.to;
        eta = h(
          "div",
          { class: "kk-track__eta" },
          h("span", { class: "kk-track__eta-label", text: "Estimated arrival" }),
          h("span", { class: "kk-track__eta-value", text: same ? fmtYmd(o.estimate.from) : `${fmtYmd(o.estimate.from)} – ${fmtYmd(o.estimate.to)}` }),
          h("span", {
            class: "kk-track__eta-note",
            text: o.estimate.source === "carrier" ? "From the carrier." : "Our usual timing. The carrier's date replaces this once it's scanned.",
          }),
        );
      }

      const ships = shipments.length
        ? h(
            "div",
            null,
            h("h3", { class: "kk-track__section-title", text: shipments.length > 1 ? "Packages" : "Package" }),
            h("div", { class: "kk-track__ships" }, shipments.map((s, i) => this.shipment(s, i, shipments.length))),
          )
        : null;

      const items = h(
        "div",
        null,
        h("h3", { class: "kk-track__section-title", text: "In this order" }),
        h(
          "ul",
          { class: "kk-track__items" },
          (o.items || []).map((it) =>
            h(
              "li",
              { class: "kk-track__item" },
              it.image
                ? h("img", { src: it.image, alt: it.alt || it.title, width: "64", height: "64", loading: "lazy" })
                : h("span", { class: "kk-track__item-ph", "aria-hidden": "true" }),
              h("div", null, h("p", { class: "kk-track__item-title", text: it.title }), it.variant ? h("p", { class: "kk-track__item-variant", text: it.variant }) : null),
              h("span", { class: "kk-track__item-qty", text: `× ${it.quantity}` }),
            ),
          ),
        ),
      );

      const again = h("button", {
        type: "button",
        class: "btn-brutal kk-track__again",
        text: "Track another order",
        onclick: () => {
          this.result.hidden = true;
          this.result.replaceChildren();
          this.form.hidden = false;
          this.contactInput.value = "";
          this.orderInput.select();
          this.orderInput.focus();
        },
      });

      this.result.replaceChildren(h("div", { class: "kk-track__card" }, head, progress, status, eta, ships, items, again));
      this.form.hidden = true;
      this.result.hidden = false;
      this.result.focus({ preventScroll: true });
      this.result.scrollIntoView({ block: "start", behavior: matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth" });
    }

    statusText(o) {
      const ships = o.shipments || [];
      if (o.stage === "cancelled") {
        return ["This order was cancelled. Questions about it or your refund? Write to ", this.mailLink(), "."];
      }
      if (o.problem) {
        return ["The carrier reports a problem with delivery. Write to ", this.mailLink(), " with your order number and we'll sort it out."];
      }
      if (o.stage === "placed") {
        return ["We've got your order. It goes to our US print partner shortly, and printing takes 2–5 business days."];
      }
      if (o.stage === "printing") {
        return ["Your order is being printed, just for you. The tracking link shows up here, and in your inbox, the moment it ships."];
      }
      if (o.stage === "delivered") {
        return ["Delivered. Enjoy it. Something not right with the print? Write to ", this.mailLink(), " with a photo and we'll replace it free."];
      }
      const labelOnly = ships.length && ships.every((s) => LABEL_ONLY.has(s.status));
      const parts = [
        labelOnly
          ? "Your order is packed and the shipping label is made. The carrier usually scans it within a day or two, then the tracking fills in."
          : "On its way, in a plain mailer.",
      ];
      if (o.partial) parts.push(" Part of your order has shipped; the rest follows in its own package.");
      return parts;
    }

    shipment(s, index, count) {
      const problem = ["DELAYED", "ATTEMPTED_DELIVERY", "NOT_DELIVERED", "FAILURE"].includes(s.status);
      const copy = s.number
        ? h("button", {
            type: "button",
            class: "kk-track__copy",
            text: "Copy number",
            onclick: async (e) => {
              try {
                await navigator.clipboard.writeText(s.number);
                e.currentTarget.textContent = "Copied";
              } catch {
                e.currentTarget.textContent = "Select and copy";
              }
            },
          })
        : null;

      return h(
        "section",
        { class: "kk-track__ship", "aria-label": count > 1 ? `Package ${index + 1} of ${count}` : "Package" },
        h(
          "div",
          { class: "kk-track__ship-head" },
          h(
            "p",
            { class: "kk-track__carrier" },
            h("b", { text: s.carrier || "Carrier" }),
            s.number ? " · " : "",
            s.number ? h("span", { class: "kk-track__tracking-no", text: s.number }) : "",
          ),
          h("span", { class: `kk-track__pill${problem ? " kk-track__pill--problem" : ""}`, text: s.label || "Shipped" }),
        ),
        s.url || copy
          ? h(
              "div",
              { class: "kk-track__ship-actions" },
              s.url
                ? h("a", {
                    class: "btn-brutal btn-brutal--accent",
                    href: s.url,
                    target: "_blank",
                    rel: "noopener",
                    text: `Track ${s.carrier ? "with " + s.carrier : "package"} ↗`,
                  })
                : null,
              copy,
            )
          : null,
        s.events && s.events.length
          ? h(
              "ol",
              { class: "kk-track__events", "aria-label": "Tracking updates, newest first" },
              s.events.slice(0, 8).map((e) =>
                h(
                  "li",
                  { class: "kk-track__event" },
                  h("time", { datetime: e.at, text: stamp.format(new Date(e.at)) }),
                  e.message || e.label,
                  e.place ? ` · ${e.place}` : "",
                ),
              ),
            )
          : null,
      );
    }
  }

  customElements.define("kk-track", KkTrack);
})();
